/**
 * Funções utilitárias para validação de CNPJ
 *
 * Baseado na especificação oficial da Receita Federal (IN RFB nº 2.229/2024)
 * Suporta o novo formato alfanumérico válido a partir de julho/2026
 *
 * @see https://www.gov.br/receitafederal/pt-br/acesso-a-informacao/acoes-e-programas/programas-e-atividades/cnpj-alfanumerico
 */

/** Pesos para cálculo do dígito verificador */
const pesosDV = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]

/** Valor base ASCII para conversão de caracteres */
const valorBaseASCII = 48

/**
 * Remove máscara do CNPJ (pontos, barras e hífens)
 * @param {String} cnpj - CNPJ com ou sem máscara
 * @returns {String} CNPJ sem máscara em uppercase
 */
export const removeCnpjMask = (cnpj) => {
  if (!cnpj) return ''

  return String(cnpj)
    .toUpperCase()
    .replace(/[./-]/g, '')
}

/**
 * Aplica máscara ao CNPJ
 * @param {String} cnpj - CNPJ sem máscara (14 caracteres)
 * @returns {String} CNPJ com máscara XX.XXX.XXX/XXXX-XX
 */
export const applyCnpjMask = (cnpj) => {
  if (!cnpj) return ''

  const cleaned = removeCnpjMask(cnpj)

  if (cleaned.length !== 14) return cnpj

  return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(
    5,
    8
  )}/${cleaned.slice(8, 12)}-${cleaned.slice(12, 14)}`
}

/**
 * Calcula os dígitos verificadores do CNPJ alfanumérico
 *
 * Usa o valor ASCII - 48 para converter caracteres em valores numéricos:
 * - Dígitos 0-9: valores 0-9
 * - Letras A-Z: valores 17-42
 *
 * @param {String} cnpjBase - Primeiros 12 caracteres do CNPJ (sem DV)
 * @returns {String} Os dois dígitos verificadores
 */
export const calculateCnpjDigits = (cnpjBase) => {
  const cleaned = removeCnpjMask(cnpjBase).slice(0, 12)

  if (cleaned.length !== 12) return null

  let somatorioDV1 = 0
  let somatorioDV2 = 0

  for (let i = 0; i < 12; i++) {
    const asciiDigito = cleaned.charCodeAt(i) - valorBaseASCII

    somatorioDV1 += asciiDigito * pesosDV[i + 1]
    somatorioDV2 += asciiDigito * pesosDV[i]
  }

  const dv1 = somatorioDV1 % 11 < 2 ? 0 : 11 - (somatorioDV1 % 11)

  somatorioDV2 += dv1 * pesosDV[12]

  const dv2 = somatorioDV2 % 11 < 2 ? 0 : 11 - (somatorioDV2 % 11)

  return `${dv1}${dv2}`
}

/**
 * Valida CNPJ alfanumérico
 *
 * Suporta tanto o formato numérico tradicional quanto o novo formato
 * alfanumérico da Receita Federal (válido a partir de julho/2026).
 *
 * Regras:
 * - 14 caracteres no total
 * - 12 primeiros: alfanuméricos (A-Z, 0-9)
 * - 2 últimos: numéricos (dígitos verificadores)
 * - Não pode ser todo zerado
 * - Não pode ter todos os caracteres iguais
 *
 * @param {String} cnpj - CNPJ com ou sem máscara
 * @returns {Boolean} true se válido, false se inválido
 *
 * @example
 * isValidCnpj('11.222.333/0001-81') // true
 * isValidCnpj('11222333000181')     // true
 * isValidCnpj('11222333000182')     // false (DV incorreto)
 * isValidCnpj('00000000000000')     // false (todo zerado)
 */
export const isValidCnpj = (cnpj) => {
  if (!cnpj) return false

  const cleaned = removeCnpjMask(cnpj)

  /** Regex: 12 caracteres alfanuméricos + 2 dígitos verificadores numéricos */
  if (!/^[A-Z\d]{12}\d{2}$/.test(cleaned)) return false

  /** Rejeita CNPJ todo zerado */
  if (cleaned === '00000000000000') return false

  /** Rejeita CNPJs com todos os caracteres iguais */
  if (/^(.)\1{13}$/.test(cleaned)) return false

  /** Valida dígitos verificadores */
  const dvInformado = cleaned.slice(12)
  const dvCalculado = calculateCnpjDigits(cleaned.slice(0, 12))

  return dvInformado === dvCalculado
}

/**
 * Valida formato do CNPJ (sem verificar dígitos)
 *
 * Útil para validação parcial durante digitação.
 *
 * @param {String} cnpj - CNPJ com ou sem máscara
 * @returns {Boolean} true se formato válido
 */
export const isValidCnpjFormat = (cnpj) => {
  if (!cnpj) return false

  const cleaned = removeCnpjMask(cnpj)

  return /^[A-Z\d]{12}\d{2}$/.test(cleaned)
}
